import React from 'react';
import { Column } from 'react-table';
import { OwnerGroupPortfolioStats } from '~/types/analytics';
import { Colors } from '~/enums/Colors';
import { getActiveTheme } from '~/theme/vendorTheme';
import { VENDOR_NAME_KEY } from '~/theme/vendorConstants';
import StatsBoxStyles from '~/components/StatsBox/StatsBox.module.css';
import CobuTableAdvanced from '~/components/CobuTableAdvanced/CobuTableAdvanced';
import { StatusIndicator } from '~/components/StatusIndicator/StatusIndicator';
import styles from './OwnerGroupPortfolioStatsPanel.module.css';

interface Props {
  portfolioStats: OwnerGroupPortfolioStats;
}

type GoogleRow = {
  cobuReviewsPercent: number;
  cobuReviewStarRating: number;
  nonCobuReviewStarRating: number;
};

export const OwnerGroupPortfolioStatsPanel = ({ portfolioStats }: Props) => {
  const googleRows: GoogleRow[] = [
    {
      cobuReviewsPercent: portfolioStats.googleReviews.cobuReviewsPercent,
      cobuReviewStarRating: portfolioStats.googleReviews.cobuReviewStarRating,
      nonCobuReviewStarRating: portfolioStats.googleReviews.nonCobuReviewStarRating
    }
  ];

  const isPresentNumber = (value: unknown) =>
    value !== null && value !== undefined && !Number.isNaN(Number(value));

  const googleColumns: Column<GoogleRow>[] = [
    {
      Header: 'COBU REVIEWS % OF TOTAL',
      accessor: 'cobuReviewsPercent',
      Cell: (cell) => {
        if (!isPresentNumber(cell.value)) {
          return 'N/A';
        }
        const value = Number(cell.value);
        const display = `${value.toFixed(1)}%`;
        return (
          <StatusIndicator
            value={value}
            indicatorFunctions={{
              green: (val) => val >= 15,
              yellow: (val) => val >= 10 && val <= 14.9,
              red: (val) => val <= 9.9
            }}
          >
            {display}
          </StatusIndicator>
        );
      }
    },
    {
      Header: 'COBU REVIEW STAR RATING',
      accessor: 'cobuReviewStarRating',
      Cell: (cell) => {
        if (!isPresentNumber(cell.value)) {
          return 'N/A';
        }
        const value = Number(cell.value);
        const display = value.toFixed(1);
        return (
          <StatusIndicator
            value={value}
            indicatorFunctions={{
              green: (val) => val >= 4.5,
              yellow: (val) => val >= 4.0 && val <= 4.4,
              red: (val) => val <= 3.9
            }}
          >
            {display}
          </StatusIndicator>
        );
      }
    },
    {
      Header: 'NON COBU REVIEW STAR RATING',
      accessor: 'nonCobuReviewStarRating',
      Cell: (cell) => {
        if (!isPresentNumber(cell.value)) {
          return 'N/A';
        }
        const value = Number(cell.value);
        const display = value.toFixed(1);
        return (
          <StatusIndicator
            value={value}
            indicatorFunctions={{
              green: (val) => val >= 4.5,
              yellow: (val) => val >= 4.0 && val <= 4.4,
              red: (val) => val <= 3.9
            }}
          >
            {display}
          </StatusIndicator>
        );
      }
    }
  ];

  const getThresholdClass = (value: number) => {
    if (value >= 50) return 'green';
    if (value >= 40) return 'yellow';
    return 'red';
  };

  const indicatorStyle = (variant: 'green' | 'yellow' | 'red') => {
    if (variant === 'green') {
      return { backgroundColor: 'var(--cobuGreen)', color: 'var(--cobuDarkBlue)' };
    }
    if (variant === 'yellow') {
      return { backgroundColor: 'var(--cobuYellow)', color: 'var(--cobuDarkBlue)' };
    }
    return { backgroundColor: 'var(--cobuRed)', color: 'var(--cobuWhite)' };
  };

  const activeTheme = getActiveTheme();
  const tealAccent = activeTheme.tealAccent || Colors.cobuTurquoise;
  const adoptionRate = Number(portfolioStats.engagement.adoptionRate);
  const activeRate = Number(portfolioStats.engagement.activeRate);
  const adoptionClass = getThresholdClass(adoptionRate);
  const activeClass = getThresholdClass(activeRate);

  return (
    <div className={styles.panel}>
      <div className={styles.sectionTitle}>Portfolio Engagement Rate</div>
      <div className={styles.statsRow}>
        <div
          className={`margin-top-16 ${StatsBoxStyles.box} ${styles.statsItem}`}
          style={{ backgroundColor: tealAccent }}
        >
          <div className={StatsBoxStyles.container}>
            <div className={StatsBoxStyles.content}>
              <div className={StatsBoxStyles.textContainer}>
                <div className={StatsBoxStyles.title}>Adoption Rate</div>
                <div className={StatsBoxStyles.description}>
                  Users as percent of occupied units in the last 6 months
                </div>
              </div>
              <div
                className={`${StatsBoxStyles.numberBox} ${adoptionClass}`}
                style={indicatorStyle(adoptionClass as 'green' | 'yellow' | 'red')}
              >
                {portfolioStats.engagement.adoptionRate.toString()}%
              </div>
            </div>
          </div>
        </div>
        <div
          className={`margin-top-16 ${StatsBoxStyles.box} ${styles.statsItem}`}
          style={{ backgroundColor: tealAccent }}
        >
          <div className={StatsBoxStyles.container}>
            <div className={StatsBoxStyles.content}>
              <div className={StatsBoxStyles.textContainer}>
                <div className={StatsBoxStyles.title}>Active Rate</div>
                <div className={StatsBoxStyles.description}>
                  Active users as % of total users
                </div>
              </div>
              <div
                className={`${StatsBoxStyles.numberBox} ${activeClass}`}
                style={indicatorStyle(activeClass as 'green' | 'yellow' | 'red')}
              >
                {portfolioStats.engagement.activeRate.toString()}%
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.sectionTitle}>Portfolio Google Review Performance</div>
      <div className={styles.googleTable}>
        <CobuTableAdvanced
          columns={googleColumns}
          data={googleRows}
          noData={{
            label: 'No Data',
            action: undefined,
            image: undefined
          }}
          showPagination={false}
          cellTextAlign='center'
          hideHeader={false}
          containerStyle={{ position: 'relative' }}
        />
      </div>
      {!sessionStorage.getItem(VENDOR_NAME_KEY) && (
        <div className={styles.bestPractices}>
          <div style={{ fontWeight: 800, fontSize: 18 }}>Best Practices</div>
          <ul className={styles.bestPracticesList}>
            <li className={styles.bestPracticesItem}>
              Add your monthly events in the{' '}
              <a href='https://app.livecobu.com'>
                Cobu Property Management Dashboard
              </a>
            </li>
            <li className={styles.bestPracticesItem}>
              Use 'RSVP on Cobu' messaging to promote all events with QR
              code/magic link/building code
            </li>
            <li className={styles.bestPracticesItem}>
              Include 'New Resident Welcome Flyer' in move-in packets
            </li>
            <li className={styles.bestPracticesItem}>
              Send Cobu Team monthly email lists (or integrate with Entrata)
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};
